/**
 * Inventory Supplier queries — PRD.207.
 *
 * Read-only against `inventory_suppliers`. Pure metadata; this module
 * never touches `stock_movements`. Suppliers are linked to Purchase
 * Orders / Delivery Orders via `supplier_id` (with `supplier_name` on
 * those tables continuing to act as the historical snapshot for
 * legacy free-text rows).
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row type ───

export interface InventorySupplierRow {
  id: string;
  org_id: string | null;
  supplier_name: string;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  tax_id: string | null;
  payment_terms: string | null;
  notes: string | null;
  status: string | null; // 'active' | 'inactive'
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

/**
 * Full supplier list (active + inactive) for the Suppliers page.
 * Sorted by status (active first), then supplier name.
 */
export function qInventorySuppliersList(): QuerySpec<[]> {
  return {
    key: ['inventory-suppliers-list'] as const,
    sql: `
      SELECT * FROM inventory_suppliers
      ORDER BY
        CASE WHEN COALESCE(status, 'active') = 'active' THEN 0 ELSE 1 END,
        LOWER(COALESCE(supplier_name, '')) ASC
    `,
    params: [],
    watch: ['inventory_suppliers'],
  };
}

/**
 * Active-only suppliers for selectors on PO/DO. Inactive suppliers
 * remain visible in historical PO/DO display but should not be offered
 * for new procurement documents.
 */
export function qActiveInventorySuppliers(): QuerySpec<[]> {
  return {
    key: ['inventory-suppliers-active'] as const,
    sql: `
      SELECT * FROM inventory_suppliers
      WHERE COALESCE(status, 'active') = 'active'
      ORDER BY LOWER(COALESCE(supplier_name, '')) ASC
    `,
    params: [],
    watch: ['inventory_suppliers'],
  };
}

/**
 * Single supplier by id. Returns 0 or 1 row.
 */
export function qInventorySupplierById(id: string): QuerySpec<[string]> {
  return {
    key: ['inventory-supplier-by-id', id] as const,
    sql: `SELECT * FROM inventory_suppliers WHERE id = ? LIMIT 1`,
    params: [id],
    watch: ['inventory_suppliers'],
  };
}
